/* eslint-disable react-refresh/only-export-components */
/**
 * IntelliHealth Chat Context
 *
 * React Context + Provider that wires the WebSocket service to React state.
 * Manages messages, conversations, streaming, history, and prompts.
 *
 * Follows the AuthContext pattern: interface, createContext, Provider, useXxx hook.
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  type ReactNode,
} from 'react';
import { intelliHealthChatService } from '@/services/intelliHealthChatService';
import type {
  ConnectionStatus,
  IntelliHealthChatEvent,
  ChatMessage,
  Conversation,
  Prompt,
  IntelliHealthChatState,
  IntelliHealthChatActions,
  HistoryPaginatedPayload,
  HistoryMessage,
  ConversationsListPayload,
  ConversationSwitchedPayload,
  ConversationCreatedPayload,
  ConversationDeletedPayload,
  ConversationRenamedPayload,
} from '@/types/intelliHealthChat';

// ============================================================================
// Context
// ============================================================================

type IntelliHealthChatContextType = IntelliHealthChatState & IntelliHealthChatActions;

const IntelliHealthChatContext = createContext<IntelliHealthChatContextType | undefined>(undefined);

export const useIntelliHealthChat = (): IntelliHealthChatContextType => {
  const context = useContext(IntelliHealthChatContext);
  if (context === undefined) {
    throw new Error('useIntelliHealthChat must be used within an IntelliHealthChatProvider');
  }
  return context;
};

// ============================================================================
// Provider
// ============================================================================

const DEFAULT_HISTORY_LIMIT = 50;
const DEFAULT_CONVERSATIONS_LIMIT = 20;

interface IntelliHealthChatProviderProps {
  children: ReactNode;
}

export const IntelliHealthChatProvider: React.FC<IntelliHealthChatProviderProps> = ({ children }) => {
  const isMounted = useRef(true);

  // State
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [streamingMessage, setStreamingMessage] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [isWaitingForResponse, setIsWaitingForResponse] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('disconnected');
  const [currentConversationId, setCurrentConversationId] = useState<string | null>(null);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [conversationsHasMore, setConversationsHasMore] = useState(false);
  const [conversationsLastEvaluatedKey, setConversationsLastEvaluatedKey] = useState<string | null>(null);
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [historyHasMore, setHistoryHasMore] = useState(false);
  const [oldestCursor, setOldestCursor] = useState<string | null>(null);
  const [rateLimited, setRateLimited] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // Ref for currentConversationId so callbacks always have latest value
  const conversationIdRef = useRef(currentConversationId);
  conversationIdRef.current = currentConversationId;

  // ── Message routing ──

  const handleMessage = useCallback((event: IntelliHealthChatEvent, data: unknown) => {
    if (!isMounted.current) return;

    switch (event) {
      case 'chat_response': {
        const chunk = data as string;
        setStreamingMessage((prev) => prev + chunk);
        setIsStreaming(true);
        setIsWaitingForResponse(false);
        break;
      }

      case 'chat_response_full': {
        // Signal only — finalize the streaming message
        setStreamingMessage((current) => {
          if (current) {
            const finalMessage: ChatMessage = {
              type: 'ai',
              content: current,
              created_at: new Date().toISOString(),
            };
            setMessages((prev) => [...prev, finalMessage]);
          }
          return '';
        });
        setIsStreaming(false);
        setIsWaitingForResponse(false);
        break;
      }

      case 'process_history_paginated': {
        const payload = data as HistoryPaginatedPayload;
        const historyMessages: ChatMessage[] = payload.messages.map((m: HistoryMessage) => ({
          type: m.type,
          content: m.data?.content ?? m.content ?? '',
          created_at: m.created_at,
        }));
        setMessages((prev) => [...historyMessages, ...prev]);
        setHistoryHasMore(payload.hasMore);
        setOldestCursor(payload.oldestCursor);
        break;
      }

      case 'process_history': {
        // Legacy flat format — handle defensively
        const legacyMessages = data as HistoryMessage[];
        if (Array.isArray(legacyMessages)) {
          const historyMessages: ChatMessage[] = legacyMessages.map((m) => ({
            type: m.type,
            content: m.data?.content ?? m.content ?? '',
            created_at: m.created_at,
          }));
          setMessages((prev) => [...historyMessages, ...prev]);
        }
        break;
      }

      case 'process_prompts': {
        const promptData = data as Prompt[];
        if (Array.isArray(promptData)) {
          setPrompts(promptData);
        }
        break;
      }

      case 'conversations_list': {
        const payload = data as ConversationsListPayload;
        setConversations(payload.conversations);
        setConversationsHasMore(payload.hasMore);
        setConversationsLastEvaluatedKey(payload.lastEvaluatedKey ?? null);
        if (payload.currentConversationId) {
          setCurrentConversationId(payload.currentConversationId);
        }
        break;
      }

      case 'conversation_switched': {
        const payload = data as ConversationSwitchedPayload;
        setCurrentConversationId(payload.conversationId);
        setMessages([]);
        setStreamingMessage('');
        setIsStreaming(false);
        setIsWaitingForResponse(false);
        setHistoryHasMore(false);
        setOldestCursor(null);
        // Load history for the new conversation
        intelliHealthChatService.send('get_history', { limit: DEFAULT_HISTORY_LIMIT });
        break;
      }

      case 'conversation_created': {
        const payload = data as ConversationCreatedPayload;
        const newConversation: Conversation = {
          conversationId: payload.conversationId,
          title: payload.conversationTitle,
        };
        setConversations((prev) => [newConversation, ...prev]);
        setCurrentConversationId(payload.conversationId);
        setMessages([]);
        setStreamingMessage('');
        setIsStreaming(false);
        setIsWaitingForResponse(false);
        break;
      }

      case 'conversation_deleted': {
        const payload = data as ConversationDeletedPayload;
        setConversations((prev) => prev.filter((c) => c.conversationId !== payload.conversationId));
        // If the deleted conversation is the current one, reset state
        if (conversationIdRef.current === payload.conversationId) {
          setCurrentConversationId(null);
          setMessages([]);
          setStreamingMessage('');
          setIsStreaming(false);
          setIsWaitingForResponse(false);
          setHistoryHasMore(false);
          setOldestCursor(null);
        }
        break;
      }

      case 'conversation_renamed': {
        const payload = data as ConversationRenamedPayload;
        setConversations((prev) =>
          prev.map((c) =>
            c.conversationId === payload.conversationId
              ? { ...c, title: payload.conversationTitle }
              : c,
          ),
        );
        break;
      }

      case 'limitReached': {
        setRateLimited(true);
        break;
      }
    }
  }, []);

  // ── Status handling ──

  const handleStatusChange = useCallback((status: ConnectionStatus) => {
    if (!isMounted.current) return;

    setConnectionStatus(status);

    if (status === 'connected') {
      // Clear stale state before re-fetching (prevents duplicates on reconnect)
      setMessages([]);
      setHistoryHasMore(false);
      setOldestCursor(null);
      setIsWaitingForResponse(false);
      // Fetch initial data on connection
      intelliHealthChatService.send('get_history', { limit: DEFAULT_HISTORY_LIMIT });
      intelliHealthChatService.send('list_conversations', { limit: DEFAULT_CONVERSATIONS_LIMIT });
      intelliHealthChatService.send('get_prompts', {});
    }
  }, []);

  // ── Register service callbacks ──

  useEffect(() => {
    isMounted.current = true;
    const unsubMessage = intelliHealthChatService.onMessage(handleMessage);
    const unsubStatus = intelliHealthChatService.onStatusChange(handleStatusChange);

    return () => {
      isMounted.current = false;
      unsubMessage();
      unsubStatus();
    };
  }, [handleMessage, handleStatusChange]);

  // ── Action methods ──

  const connect = useCallback((wsUrl?: string) => {
    const url = wsUrl || import.meta.env.VITE_NOVAMED_CHAT_WEBSOCKET_URL;
    if (!url) {
      setError('WebSocket URL not configured');
      return;
    }
    const token = localStorage.getItem('authToken') ?? undefined;
    setError(null);
    setRateLimited(false);
    intelliHealthChatService.connect(url, token);
  }, []);

  const disconnect = useCallback(() => {
    intelliHealthChatService.disconnect();
  }, []);

  const retry = useCallback(() => {
    intelliHealthChatService.disconnect();
    connect();
  }, [connect]);

  // ── Auto-connect when drawer opens ──

  useEffect(() => {
    if (isDrawerOpen && connectionStatus === 'disconnected') {
      connect();
    }
  }, [isDrawerOpen, connectionStatus, connect]);

  const sendMessage = useCallback((text: string, patientId?: string, tabContext?: string) => {
    if (!conversationIdRef.current) {
      console.warn('[IntelliHealthChat] No active conversation, cannot send message');
      return;
    }

    const payload: Record<string, unknown> = {
      data: text,
      conversationId: conversationIdRef.current,
    };
    if (patientId) payload.patientId = patientId;
    if (tabContext) payload.tabContext = tabContext;

    const sent = intelliHealthChatService.send('send_message', payload);
    if (sent) {
      // Optimistically add the user message
      const userMessage: ChatMessage = {
        type: 'human',
        content: text,
        created_at: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, userMessage]);
      setIsWaitingForResponse(true);
    }
  }, []);

  const loadMoreHistory = useCallback(() => {
    if (!historyHasMore || !oldestCursor) return;
    intelliHealthChatService.send('get_history', {
      limit: DEFAULT_HISTORY_LIMIT,
      before: oldestCursor,
    });
  }, [historyHasMore, oldestCursor]);

  const listConversations = useCallback((lastEvaluatedKey?: string) => {
    const payload: Record<string, unknown> = { limit: DEFAULT_CONVERSATIONS_LIMIT };
    if (lastEvaluatedKey) payload.lastEvaluatedKey = lastEvaluatedKey;
    intelliHealthChatService.send('list_conversations', payload);
  }, []);

  const switchConversation = useCallback((conversationId: string) => {
    intelliHealthChatService.send('switch_conversation', { conversationId });
  }, []);

  const createConversation = useCallback((title?: string) => {
    const payload: Record<string, unknown> = {};
    if (title) payload.title = title;
    intelliHealthChatService.send('create_conversation', payload);
  }, []);

  const deleteConversation = useCallback((conversationId: string) => {
    intelliHealthChatService.send('delete_conversation', { conversationId });
  }, []);

  const renameConversation = useCallback((conversationId: string, title: string) => {
    intelliHealthChatService.send('rename_conversation', { conversationId, title });
  }, []);

  const toggleDrawer = useCallback(() => {
    setIsDrawerOpen((prev) => !prev);
  }, []);

  const setDrawerOpen = useCallback((open: boolean) => {
    setIsDrawerOpen(open);
  }, []);

  // ── Context value ──

  const value: IntelliHealthChatContextType = {
    // State
    messages,
    streamingMessage,
    isStreaming,
    isWaitingForResponse,
    connectionStatus,
    currentConversationId,
    conversations,
    conversationsHasMore,
    conversationsLastEvaluatedKey,
    prompts,
    historyHasMore,
    oldestCursor,
    rateLimited,
    error,
    isDrawerOpen,
    // Actions
    connect,
    disconnect,
    retry,
    sendMessage,
    loadMoreHistory,
    listConversations,
    switchConversation,
    createConversation,
    deleteConversation,
    renameConversation,
    toggleDrawer,
    setDrawerOpen,
  };

  return (
    <IntelliHealthChatContext.Provider value={value}>
      {children}
    </IntelliHealthChatContext.Provider>
  );
};
